import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthReady } from './useAuthReady';
import { logFunnelEvent } from '@/utils/funnelEvents';

export type ChecklistState = {
  step1?: boolean;
  step2?: boolean;
  step3?: boolean;
  completed_at?: string | null;
  dismissed_at?: string | null;
  /** Set the first time the user closes the differentiator IntroSlideshow. */
  intro_seen_at?: string | null;
};

export function useOnboardingChecklist() {
  const { user, isReady } = useAuthReady();
  const [state, setState] = useState<ChecklistState | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isReady) return;
    if (!user) { setState(null); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_preferences')
        .select('onboarding_checklist')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      setState(((data?.onboarding_checklist as ChecklistState) ?? {}) || {});
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isReady, user]);

  const persist = useCallback(async (next: ChecklistState) => {
    if (!user) return;
    setState(next);
    await supabase
      .from('user_preferences')
      .upsert(
        { user_id: user.id, onboarding_checklist: next as any },
        { onConflict: 'user_id' }
      );
  }, [user]);

  const setStep = useCallback(async (step: 'step1' | 'step2' | 'step3', value: boolean) => {
    const prev = state ?? {};
    const next: ChecklistState = { ...prev, [step]: value };
    const allDone = !!next.step1 && !!next.step2 && !!next.step3;
    const wasAllDone = !!prev.step1 && !!prev.step2 && !!prev.step3;
    if (allDone && !next.completed_at) next.completed_at = new Date().toISOString();
    await persist(next);

    // Fire onboarding nudge events (Proactive Monitor handles 24h cooldown).
    if (value && !prev[step]) {
      try {
        const stepNum = step === 'step1' ? 1 : step === 'step2' ? 2 : 3;
        window.dispatchEvent(new CustomEvent('proactive:onboarding-step-completed', {
          detail: { step: stepNum, allDone: allDone && !wasAllDone },
        }));
        logFunnelEvent('checklist_step_completed', { step: stepNum });
        if (allDone && !wasAllDone) logFunnelEvent('checklist_completed', {});
      } catch { /* noop */ }
    }
  }, [state, persist]);

  const dismiss = useCallback(async () => {
    await persist({ ...(state ?? {}), dismissed_at: new Date().toISOString() });
  }, [state, persist]);

  const markIntroSeen = useCallback(async () => {
    const prev = state ?? {};
    if (prev.intro_seen_at) return;
    await persist({ ...prev, intro_seen_at: new Date().toISOString() });
    try { logFunnelEvent('intro_slideshow_dismissed', {}); } catch { /* noop */ }
  }, [state, persist]);

  const isComplete = !!(state?.completed_at || state?.dismissed_at);
  const introSeen = !!state?.intro_seen_at;

  return { state, loading, setStep, dismiss, markIntroSeen, isComplete, introSeen };
}
